import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi, setTokens, clearTokens } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Rehydrate from stored tokens on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    authApi.me()
      .then((data) => setUser(data.user))
      .catch(() => { clearTokens(); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  // Listen for forced logout (401 + refresh failed in api layer)
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  const login = useCallback(async (identifier, password) => {
    const data = await authApi.login({ username: identifier, password });
    setTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
    setUser(data.user);
    return data.user;
  }, []);

  // Backend now requires email and phone_number on registration.
  // `exam_intent` (optional) is passed through so the lead record gets context.
  // `campaign` (optional, C1) attaches acquisition-campaign registrants to the
  // campaign org. Returns the full response so callers can read
  // `campaign.joined` — whether a free place was actually secured.
  const register = useCallback(async (username, email, password, phone_number, exam_intent, campaign) => {
    const data = await authApi.register({
      username,
      email,
      password,
      phone_number,
      exam_intent,
      ...(campaign ? { campaign } : {}),
    });
    setTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    clearTokens();
    setUser(null);
  }, []);

  // Chunk 44: replace the cached user after a profile change (e.g. Qudurat
  // language) so every consumer sees the new value without a /me round-trip.
  const updateUser = useCallback((u) => setUser(u), []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}